// EntityCombobox: the kit Combobox (components/ui/Combobox) under its older call shape, for the
// pages not yet moved onto it. The behaviour and the list are the kit's; this keeps the visible
// label above the field and honours `inputClassName`, which draws the caller's own field with
// no icon or chevron. docs/features/shared-combobox.md.
//
// NOT named LocationPicker, though its first caller picks locations: components/LocationPicker.js
// is a Leaflet map picker for show coordinates.

import React, { useState } from 'react';
import Combobox from './ui/Combobox';
import { labelClass } from './ui/Field';
import { IconSearch } from './ui/icons';
import type { ComboboxOption } from './ui/ComboboxList';

export type { ComboboxOption } from './ui/ComboboxList';

let seq = 0;

interface Props {
  options: ComboboxOption[];
  /** Currently chosen option id, or '' / null for none. */
  value: string | number | null;
  onSelect: (option: ComboboxOption | null) => void;
  placeholder?: string;
  /** Rendered above the field. Also the accessible name. */
  label?: string;
  /** Whether focus alone opens the list. Default true. */
  openOnFocus?: boolean;
  id?: string;
  disabled?: boolean;
  /** Shown in the list's place when a query has been typed and nothing matches. */
  emptyMessage?: React.ReactNode;
  /** Shown in the list's place when there are no options at all. */
  noOptionsMessage?: React.ReactNode;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  /** Match against sublabel and chip too, not just label. Default true. */
  searchSublabel?: boolean;
  autoFocus?: boolean;
  /** Filter `options` by the typed query. Default true; false for a server search. */
  filterOptions?: boolean;
  /** The typed query, on every keystroke, and '' when it is cleared. */
  onQueryChange?: (query: string) => void;
}

const EntityCombobox: React.FC<Props> = ({
  label,
  labelClassName,
  inputClassName,
  className = '',
  id,
  placeholder = 'Type to search',
  filterOptions = true,
  ...rest
}) => {
  const [made] = useState(() => `combo-${++seq}`);
  const fieldId = id || made;
  // A server search is a search, not a choice from a set: a magnifier rather than a chevron.
  const search = !filterOptions;
  return (
    <div className={`min-w-0 ${className}`}>
      {label && (
        <label className={labelClassName || `${labelClass} mb-1.5`} htmlFor={fieldId}>
          {label}
        </label>
      )}
      <Combobox
        {...rest}
        id={fieldId}
        label={label || placeholder}
        placeholder={placeholder}
        filterOptions={filterOptions}
        chevron={!search}
        leading={search ? <IconSearch className="h-3.5 w-3.5" /> : undefined}
        legacyFieldClass={inputClassName || undefined}
      />
    </div>
  );
};

export default EntityCombobox;
